import React, { useState } from "react";
import { ArrowDown, ArrowUp, CalendarDays, ChevronDown, ChevronUp, FileText, MapPin, Pencil, Trash2 } from "lucide-react";
import { formatDate } from "../../../utils/tspFormat";
import { formatCoordinate } from "../../../utils/tspCoordinates";
import { placeSubtitle, placeTitle, validatePlaceDate } from "./journeyBuilderModel";
import JourneyPlaceSections from "./JourneyPlaceSections";
import JourneyCitySummary from "../components/JourneyCitySummary";
import useTspPermission from "../../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../../constants/tspPermissions";

export { PLACE_TABS } from "./JourneyPlaceSections";

const VISIT_STATE_LABEL = { visited: "Visited", current: "Current place", not_visited: "Upcoming" };

// One place of the journey as a card: its name, district / state and visit
// date; the five section tabs (JourneyPlaceSections) — a tab opens its section
// INSIDE this card. Each card keeps its own open tab and its own section
// drafts: opening a section of one place never touches another place.
//
// `usePlaceStore(place, load)` supplies the place's records and actions (staged
// on Add Journey, live on Edit Journey) — see JourneyPlaceSections.
//
// onDateChange(date) returns { ok } | { ok: false, message }.
// `onChangePlace` (Add Journey, a place with a map point): back into the picker.
export default function JourneyPlaceCard({
  place,
  order,
  total,
  expanded,
  onToggle,
  activeTab,
  onTabChange,
  usePlaceStore,
  perms,
  range,
  bookCatalog,
  canEditRoute,
  onDateChange,
  onStatusChange,
  onRemove,
  onMove,
  onChangePlace,
  tabErrors = {},
  dateError = "",
  disabled = false,
}) {
  const idBase = `jb-${place.key}`;
  const title = placeTitle(place);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const { hasPermission } = useTspPermission();
  const canViewSummary = hasPermission(TSP_PERMISSIONS.KNOWLEDGE_REPOSITORY_VIEW);
  const locationId = place.locationId || place.location_id;

  const currentVal =
    place.visitState === "visited" || place.status === "completed"
      ? "completed"
      : place.visitState === "current" || place.status === "live"
      ? "live"
      : "not_visited";

  const handleSelectStatus = (e) => {
    e.stopPropagation();
    const newStatus = e.target.value;
    if (newStatus === currentVal) return;
    onStatusChange?.(place, newStatus);
  };

  return (
    <li className={`tsp-pc${expanded ? " is-open" : ""}${dateError || Object.keys(tabErrors).length ? " is-invalid" : ""}`} aria-labelledby={`${idBase}-name`}>
      <div className="tsp-pc-head">
        <span className="tsp-route-stop-order" aria-hidden="true">
          {order}
        </span>
        <button type="button" className="tsp-pc-toggle" onClick={onToggle} aria-expanded={expanded} aria-controls={`${idBase}-body`}>
          <span className="tsp-pc-name" id={`${idBase}-name`}>
            {title}
          </span>
          {placeSubtitle(place) && <span className="tsp-pc-where">{placeSubtitle(place)}</span>}
          <span className="tsp-sr-only">{expanded ? "Hide the details of this place" : "Show the details of this place"}</span>
        </button>
        <span className="tsp-pc-actions">
          {onChangePlace && Number.isFinite(place.latitude) && Number.isFinite(place.longitude) && (
            <button type="button" className="tsp-icon-link-btn" onClick={onChangePlace} disabled={disabled} aria-label={`Change the place of ${title}`}>
              <Pencil size={14} />
            </button>
          )}
          {canEditRoute && onMove && (
            <>
              <button type="button" className="tsp-icon-link-btn" onClick={() => onMove(-1)} disabled={disabled || order === 1} aria-label={`Move ${title} up`}>
                <ArrowUp size={14} />
              </button>
              <button type="button" className="tsp-icon-link-btn" onClick={() => onMove(1)} disabled={disabled || order === total} aria-label={`Move ${title} down`}>
                <ArrowDown size={14} />
              </button>
            </>
          )}
          {canEditRoute && onRemove && (
            <button type="button" className="tsp-icon-link-btn tsp-row-delete-btn" onClick={onRemove} disabled={disabled} aria-label={`Remove ${title}`}>
              <Trash2 size={14} />
            </button>
          )}
          {canViewSummary && locationId && (
            <button
              type="button"
              className={`tsp-btn tsp-btn-outline-info tsp-btn-sm tsp-city-row-action${summaryOpen ? " is-active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setSummaryOpen(!summaryOpen);
              }}
              aria-expanded={summaryOpen}
              aria-label={`${summaryOpen ? "Hide" : "View"} Summary for ${title}`}
            >
              <FileText size={13} aria-hidden="true" /> {summaryOpen ? "Hide Summary" : "View Summary"}
              {summaryOpen ? <ChevronUp size={13} aria-hidden="true" /> : <ChevronDown size={13} aria-hidden="true" />}
            </button>
          )}
          <span className="tsp-city-status-selector-wrapper" onClick={(e) => e.stopPropagation()}>
            <select
              className={`tsp-city-status-select tsp-city-status-select-${currentVal}`}
              value={currentVal}
              onChange={handleSelectStatus}
              disabled={disabled || !onStatusChange}
              aria-label={`Update status for ${title}`}
              title="Update place status"
            >
              <option value="live">In Progress</option>
              <option value="completed">Completed</option>
              <option value="not_visited">Upcoming</option>
            </select>
          </span>
          <button type="button" className="tsp-icon-link-btn" onClick={onToggle} aria-hidden="true" tabIndex={-1}>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </span>

        <div className="tsp-pc-meta">
          <PlaceDate place={place} idBase={idBase} canEdit={canEditRoute} onSave={onDateChange} disabled={disabled} error={dateError} />
          {place.visitState && <span className={`tsp-status-pill tsp-pc-state tsp-pc-state-${place.visitState}`}>{VISIT_STATE_LABEL[place.visitState] || place.visitState}</span>}
          {Number.isFinite(place.latitude) && Number.isFinite(place.longitude) && (
            <span className="tsp-pc-point" title="Map location">
              <MapPin size={12} aria-hidden="true" /> {formatCoordinate(place.latitude)}, {formatCoordinate(place.longitude)}
            </span>
          )}
        </div>
      </div>

      {summaryOpen && locationId && <JourneyCitySummary locationId={locationId} cityName={title} />}


      <div className="tsp-pc-body" id={`${idBase}-body`} hidden={!expanded}>
        <JourneyPlaceSections
          place={place}
          idBase={idBase}
          title={title}
          activeTab={activeTab}
          onTabChange={onTabChange}
          usePlaceStore={usePlaceStore}
          perms={perms}
          range={range}
          bookCatalog={bookCatalog}
          tabErrors={tabErrors}
          disabled={disabled}
        />
      </div>
    </li>
  );
}

// The place's visit date, changed in place (Edit -> date -> Save).
function PlaceDate({ place, idBase, canEdit, onSave, disabled, error }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(place.plannedDate || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const inputId = `${idBase}-date`;

  const save = async () => {
    const problem = validatePlaceDate(value);
    if (problem) {
      setMessage(problem);
      return;
    }
    setBusy(true);
    const result = await onSave(value);
    setBusy(false);
    if (result && result.ok === false) {
      setMessage(result.message);
      return;
    }
    setEditing(false);
    setMessage("");
  };

  if (editing) {
    return (
      <span className="tsp-pc-date-edit">
        <label htmlFor={inputId} className="tsp-pc-date-label">
          Visit Date
        </label>
        <input
          id={inputId}
          type="date"
          className="tsp-input"
          value={value}
          disabled={busy}
          onChange={(event) => { setValue(event.target.value); setMessage(""); }}
          onKeyDown={(event) => {
            // Enter saves the date — it must not submit the whole journey.
            if (event.key === "Enter") {
              event.preventDefault();
              save();
            }
          }}
        />
        <button type="button" className="tsp-btn tsp-btn-primary tsp-btn-sm" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" className="tsp-btn tsp-btn-outline tsp-btn-sm" onClick={() => { setEditing(false); setValue(place.plannedDate || ""); setMessage(""); }} disabled={busy}>
          Cancel
        </button>
        {message && (
          <span className="tsp-field-error" role="alert">
            {message}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className="tsp-pc-date">
      <CalendarDays size={13} aria-hidden="true" />
      <span>
        Visit Date: <strong>{place.plannedDate ? formatDate(place.plannedDate) : "Not set"}</strong>
      </span>
      {canEdit && onSave && (
        <button type="button" className="tsp-icon-link-btn" onClick={() => { setValue(place.plannedDate || ""); setEditing(true); }} disabled={disabled} aria-label={`Change the visit date of ${placeTitle(place)}`}>
          <Pencil size={12} />
        </button>
      )}
      {error && (
        <span className="tsp-field-error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}
